<template>
    <nav class="nav-desktop" aria-label="Main">
        <div class="nav-desktop-inner">
            <ul>
                <li v-for="item in items" :key="item.label">
                    <a
                        v-if="item.external"
                        :href="item.to"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {{ item.label }}
                    </a>
                    <NuxtLink
                        v-else
                        :to="item.to"
                        :class="{ 'is-active': isActive(item) }"
                        :aria-current="isActive(item) ? 'page' : undefined"
                    >
                        {{ item.label }}
                    </NuxtLink>
                </li>
            </ul>
        </div>
    </nav>
</template>

<script setup>
    const route = useRoute();

    // Order and labels per Figma 4933-40092 (Navigation (Desktop) → Frame 2710).
    const items = [
        { label: 'OHC 2026', to: '/', exact: true },
        { label: 'My OHC', to: '/my-ohc/' },
        { label: 'Sites', to: '/sites/' },
        { label: 'Neighborhoods', to: '/neighborhoods/' },
        { label: 'Itineraries', to: '/itineraries/' },
        { label: 'Events', to: '/events/' },
        // Same destination the slide-out menu already uses for this.
        { label: 'Photo Competition', to: 'https://www.architecture.org/open-house-chicago/photo-competition-2026', external: true },
        // Was /about-ohc, which rendered architecture.org's about page inside a
        // full-page iframe. Straight out to the real page instead.
        { label: 'About', to: 'https://www.architecture.org/open-house-chicago/about', external: true },
    ];

    // Detail pages keep their section lit, so match on prefix — except the
    // first item, which would otherwise match every route.
    const isActive = (item) => {
        const path = route.path.endsWith('/') ? route.path : `${route.path}/`;
        if (item.exact) return path === '/';
        return path.startsWith(item.to);
    };
</script>

<style lang="scss" scoped>
    // Desktop main nav: the teal bar under the header. Hidden on mobile, where
    // nav-chips.vue covers the same ground. Its height feeds --nav-row-height in
    // layouts/default.vue, which is what pushes #main clear of the fixed chrome.
    // Desktop only. Mobile and tablet use the hamburger drawer instead, so this
    // starts at $desktop rather than $tablet.
    .nav-desktop {
        display: none;

        @media (min-width: $desktop) {
            display: block;

            position: fixed;
            top: var(--header-height);
            left: 0;
            right: 0;
            z-index: 98; // header is 99

            height: var(--nav-row-height);
            background: $turquoise;
        }
    }

    .nav-desktop-inner {
        max-width: $hd;
        height: 100%;
        margin: 0 auto;
        padding: 0 16px;

        // $hd + the two gutters, matching .hd-container and the header above —
        // these two have to switch at the same width or the logo stops lining
        // up with the nav row beneath it.
        @media (min-width: $hd + 32px) {
            padding: 0;
        }
    }

    ul {
        display: flex;
        align-items: center;
        gap: 24px;
        height: 100%;

        // Safety valve between $tablet and the width the full row needs (~800px
        // of content). Scrolls rather than wrapping or clipping.
        overflow-x: auto;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    li {
        flex: 0 0 auto;
    }

    a {
        display: block;
        padding: 4px 0;

        font-family: $title;
        font-size: 17px;
        font-weight: $bold;
        line-height: 1;
        letter-spacing: 0.3px;
        text-transform: uppercase;
        white-space: nowrap;
        color: $white;

        border-bottom: 2px solid transparent;

        &.is-active {
            border-bottom-color: $white;
        }

        &:hover {
            border-bottom-color: rgba(255, 255, 255, 0.5);
        }

        &:focus-visible {
            outline: 2px solid $white;
            outline-offset: 3px;
        }
    }
</style>
